import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

type Row = { id: string; query: string; user_id: string; searched_at: string };

export default function AdminSearches() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('search_history')
        .select('id, query, user_id, searched_at')
        .order('searched_at', { ascending: false })
        .limit(500);
      setRows((data as Row[]) || []);
      setLoading(false);
    })();
  }, []);

  // Top queries
  const topQueries = (() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => {
      const k = r.query.trim().toLowerCase();
      counts.set(k, (counts.get(k) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10);
  })();

  // Last 7 days
  const last7 = (() => {
    const days = new Map<string, number>();
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today); d.setDate(today.getDate() - i);
      days.set(d.toISOString().slice(0, 10), 0);
    }
    rows.forEach((r) => {
      const k = r.searched_at.slice(0, 10);
      if (days.has(k)) days.set(k, (days.get(k) || 0) + 1);
    });
    return [...days.entries()];
  })();

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>;

  const max7 = Math.max(1, ...last7.map(([, v]) => v));

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Search Analytics</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" />Top Queries</CardTitle></CardHeader>
          <CardContent>
            {topQueries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No searches yet</p>
            ) : (
              <ol className="space-y-2">
                {topQueries.map(([q, n], i) => (
                  <li key={q} className="flex items-center justify-between text-sm">
                    <span><span className="text-muted-foreground mr-2">{i + 1}.</span>{q}</span>
                    <span className="font-semibold">{n}</span>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">Last 7 days</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-2 h-32">
              {last7.map(([d, v]) => (
                <div key={d} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-primary/80 rounded-t" style={{ height: `${(v / max7) * 100}%`, minHeight: v ? 4 : 0 }} title={`${v} searches`} />
                  <span className="text-[10px] text-muted-foreground">{d.slice(5)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="rounded-lg border bg-card overflow-x-auto">
        <h3 className="p-3 font-semibold border-b">Recent searches (last 500)</h3>
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr className="text-left"><th className="p-3">Query</th><th className="p-3">User ID</th><th className="p-3">When</th></tr></thead>
          <tbody>
            {rows.slice(0, 100).map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-3">{r.query}</td>
                <td className="p-3 text-xs text-muted-foreground font-mono">{r.user_id.slice(0, 8)}…</td>
                <td className="p-3 text-muted-foreground">{new Date(r.searched_at).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}